import React from 'react';
import { X, Crown, Gift, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { SUBSCRIPTION_PLAN_IDS } from '../../types/subscription';
import { getProfessionalPlan, getFreePlan } from '../../constants/subscriptionPlans';
import { colors } from '../../styles/colors';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
  onContinueFree: () => void;
}

const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  isOpen,
  onClose,
  onSubscribe,
  onContinueFree
}) => {
  const navigate = useNavigate();
  const { language, isRTL } = useLocalization();
  const { upgradePlan } = useSubscription();

  const professionalPlan = getProfessionalPlan();
  const freePlan = getFreePlan();

  if (!isOpen) return null;

  const handleSubscribe = async () => {
    try {
      await upgradePlan(SUBSCRIPTION_PLAN_IDS.PROFESSIONAL);
      onSubscribe();
      // Navigate to subscription page
      navigate('/dashboard/subscriptions');
    } catch (error) {
      console.error('Error upgrading to professional plan:', error);
    }
  };

  const handleContinueFree = () => {
    onContinueFree();
    // Navigate to dashboard since subscription is already downgraded
    navigate('/dashboard');
  };

  const strings = {
    en: {
      title: 'Your Free Trial Has Ended',
      subtitle: 'Your subscription has been automatically downgraded to the free plan. Choose how you\'d like to continue:',
      professional: {
        title: 'Continue with Professional Plan',
        price: `${professionalPlan.price} EGP/month`,
        features: [
          'Unlimited Offices',
          'Unlimited Clients', 
          'Unlimited Files',
          'Advanced Statistics',
          'Team Management',
          'Phone Support',
          'Export Excel'
        ],
        button: 'Subscribe Now'
      },
      free: {
        title: 'Continue with Free Plan (Currently Active)',
        description: 'Your subscription is now on the free plan with limited features',
        limits: [
          '1 Office maximum',
          '10 Clients maximum',
          '3 Files maximum',
          'Basic features only'
        ],
        button: 'Continue with Free Plan'
      },
      close: 'Close'
    },
    ar: {
      title: 'انتهت فترة التجربة المجانية',
      subtitle: 'تم تخفيض اشتراكك تلقائياً إلى الخطة المجانية. اختر كيف تريد المتابعة:',
      professional: {
        title: 'المتابعة مع الخطة المهنية',
        price: `${professionalPlan.price} جنيه/شهر`,
        features: [
          'مكاتب غير محدودة',
          'عملاء غير محدودين',
          'ملفات غير محدودة',
          'إحصائيات متقدمة',
          'إدارة الفريق',
          'دعم الهاتف',
          'تصدير إكسل'
        ],
        button: 'اشترك الآن'
      },
      free: {
        title: 'المتابعة مع الخطة المجانية (نشطة حالياً)',
        description: 'اشتراكك الآن على الخطة المجانية بميزات محدودة',
        limits: [
          'مكتب واحد كحد أقصى',
          '10 عملاء كحد أقصى',
          '3 ملفات كحد أقصى',
          'ميزات أساسية فقط'
        ],
        button: 'متابعة مع الخطة المجانية'
      },
      close: 'إغلاق'
    }
  };

  const t = strings[language];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        style={{ 
          backgroundColor: colors.white,
          color: colors.black,
          border: `1px solid ${colors.lightGrey}`
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: colors.lightGrey }}
        >
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <div 
              className="p-2 rounded-full"
              style={{ backgroundColor: `${colors.orange}20` }}
            >
              <AlertTriangle 
                className="w-6 h-6" 
                style={{ color: colors.orange }}
              />
            </div>
            <div>
              <h2 
                className="text-xl font-bold"
                style={{ color: colors.primary }}
              >
                {t.title}
              </h2>
              <p 
                className="text-sm"
                style={{ color: colors.darkGrey }}
              >
                {t.subtitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" style={{ color: colors.darkGrey }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Professional Plan Option */}
            <div 
              className="border-2 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
              style={{ 
                borderColor: colors.accent,
                backgroundColor: `${colors.accent}05`
              }}
            >
              <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                <div 
                  className="p-2 rounded-full"
                  style={{ backgroundColor: `${colors.accent}20` }}
                >
                  <Crown 
                    className="w-6 h-6" 
                    style={{ color: colors.accent }}
                  />
                </div>
                <div>
                  <h3 
                    className="text-lg font-semibold"
                    style={{ color: colors.primary }}
                  >
                    {t.professional.title}
                  </h3>
                  <p 
                    className="text-2xl font-bold"
                    style={{ color: colors.accent }}
                  >
                    {t.professional.price}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {t.professional.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: colors.accent }}
                    ></div>
                    <span 
                      className="text-sm"
                      style={{ color: colors.black }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleSubscribe}
                className="w-full text-white font-medium py-3 px-4 rounded-lg transition-colors hover:shadow-md bg-primary hover:bg-primary-dark"
              >
                {t.professional.button}
              </button>
            </div>

            {/* Free Plan Option */}
            <div 
              className="border-2 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
              style={{ 
                borderColor: colors.lightGrey,
                backgroundColor: `${colors.lightGrey}10`
              }}
            >
              <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
                <div 
                  className="p-2 rounded-full"
                  style={{ backgroundColor: `${colors.lightGrey}40` }}
                >
                  <Gift 
                    className="w-6 h-6" 
                    style={{ color: colors.darkGrey }}
                  />
                </div>
                <div>
                  <h3 
                    className="text-lg font-semibold"
                    style={{ color: colors.primary }}
                  >
                    {t.free.title}
                  </h3>
                  <p 
                    className="text-sm"
                    style={{ color: colors.darkGrey }}
                  >
                    {t.free.description}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {t.free.limits.map((limit, index) => (
                  <li key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: colors.darkGrey }}
                    ></div>
                    <span 
                      className="text-sm"
                      style={{ color: colors.darkGrey }}
                    >
                      {limit}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleContinueFree}
                className="w-full text-white font-medium py-3 px-4 rounded-lg transition-colors hover:shadow-md"
                style={{ 
                  backgroundColor: colors.darkGrey,
                  border: `1px solid ${colors.darkGrey}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.grey;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.darkGrey;
                }}
              >
                {t.free.button}
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div 
            className="mt-6 p-4 rounded-lg"
            style={{ 
              backgroundColor: `${colors.primaryLight}20`,
              border: `1px solid ${colors.primaryLight}40`
            }}
          >
            <p 
              className="text-sm text-center"
              style={{ color: colors.primary }}
            >
              {isRTL 
                ? 'يمكنك تغيير خطتك في أي وقت من صفحة الاشتراكات'
                : 'You can change your plan anytime from the subscriptions page'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;